/**
 * Command Centre presentation components.
 *
 * Two rules enforced here so no screen can breach them:
 *  - `null` renders as NO DATA, never as 0 or £0.
 *  - Every figure states its cohort and basis, so a number can never be read
 *    as measuring something it does not.
 */

export interface KpiSpec {
  readonly value: number | null;
  readonly cohort: 'IN_COHORT' | 'PRE_LAUNCH' | 'ALL';
  readonly basis: string;
}

const gbp = (n: number): string =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n);
const num = (n: number): string => new Intl.NumberFormat('en-GB').format(n);

export function CostKpi({ label, kpi }: { label: string; kpi: KpiSpec }): JSX.Element {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      {kpi.value === null
        ? <div className="kpi-value nodata" title={kpi.basis}>NO DATA</div>
        : <div className="kpi-value">{gbp(kpi.value)}</div>}
      <div className="kpi-note">{kpi.basis}</div>
    </div>
  );
}

export function CountKpi({
  label, value, note, money = false, dp,
}: { label: string; value: number | null; note?: string; money?: boolean; dp?: number }): JSX.Element {
  /* Some figures lose their meaning when rounded to whole units: a cost per
     click of £11.39 shown as £11 is a 3% error on the number the bidding
     decision turns on. */
  const fmt = (n: number): string => dp === undefined
    ? (money ? gbp(n) : num(n))
    : new Intl.NumberFormat('en-GB', money
        ? { style: 'currency', currency: 'GBP', minimumFractionDigits: dp, maximumFractionDigits: dp }
        : { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(n);
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      {value === null
        ? <div className="kpi-value nodata">NO DATA</div>
        : <div className="kpi-value">{fmt(value)}</div>}
      {note ? <div className="kpi-note">{note}</div> : null}
    </div>
  );
}

export function Panel({
  title, subtitle, children,
}: { title: string; subtitle?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="panel">
      <h2>{title}</h2>
      {subtitle ? <p>{subtitle}</p> : null}
      <div style={{ marginTop: 14 }}>{children}</div>
    </div>
  );
}

export function CohortBadge({ cohort }: { cohort: string }): JSX.Element {
  const label = cohort === 'IN_COHORT' ? 'Growth Engine cohort' : cohort === 'PRE_LAUNCH' ? 'Pre-launch (historical)' : 'All data';
  const cls = cohort === 'IN_COHORT' ? 'pill good' : cohort === 'PRE_LAUNCH' ? 'pill warn' : 'pill';
  return <span className={cls}>{label}</span>;
}

/** Confidence bar. `unattributed` is a segment of its own and never merged. */
export function ConfidenceBar({
  known, likely, unknown, unattributed,
}: { known: number; likely: number; unknown: number; unattributed: number }): JSX.Element {
  const total = known + likely + unknown + unattributed;
  if (total === 0) return <span style={{ color: 'var(--text-faint)' }}>NO DATA</span>;
  const pct = (n: number): string => `${(n / total) * 100}%`;
  const seg = (n: number, colour: string, title: string): JSX.Element | null =>
    n === 0 ? null : <div style={{ width: pct(n), background: colour, height: 10 }} title={title} />;
  return (
    <div>
      <div style={{ display: 'flex', width: '100%', borderRadius: 3, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {seg(known, 'var(--positive)', `KNOWN ${known}`)}
        {seg(likely, 'var(--accent)', `LIKELY ${likely}`)}
        {seg(unknown, 'var(--warning)', `UNKNOWN ${unknown}`)}
        {seg(unattributed, 'var(--border-strong)', `UNATTRIBUTED ${unattributed}`)}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--positive)' }}>KNOWN {known}</span>
        <span style={{ color: 'var(--accent)' }}>LIKELY {likely}</span>
        {unknown > 0 ? <span style={{ color: 'var(--warning)' }}>UNKNOWN {unknown}</span> : null}
        <span style={{ color: 'var(--text-faint)' }}>UNATTRIBUTED {unattributed}</span>
      </div>
    </div>
  );
}
